import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Loader2, LogIn, LogOut, ShieldCheck, ShieldAlert, Lock, ArrowLeft,
  Calendar, Building2, Users, CheckCircle2, AlertCircle, XCircle, Ticket, Printer, Clock,
  FileSignature, Home, UserCircle, Download,
} from 'lucide-react';
import PdfPreviewModal from '../components/PdfPreviewModal';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOF_TOKEN_KEY = 'hof_seat_token';
const HOF_INFO_KEY = 'hof_seat_info';

const hofApi = (token) => axios.create({
  baseURL: API_BASE,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

// ============== LOGIN VIEW ==============
const LoginView = ({ onLoggedIn }) => {
  const [its, setIts] = useState('');
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState('');   // 409 message from server

  const submit = async (e) => {
    e.preventDefault();
    setConflict('');
    if (!its.trim() || !pwd) { toast.error('ITS and password are required'); return; }
    setBusy(true);
    try {
      const res = await hofApi('').post('/seat/hof/login', { its_id: its.trim(), password: pwd });
      const { token, hof, must_change_password } = res.data;
      localStorage.setItem(HOF_TOKEN_KEY, token);
      localStorage.setItem(HOF_INFO_KEY, JSON.stringify({ hof, must_change_password }));
      onLoggedIn(token, hof, must_change_password);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || 'Login failed';
      if (status === 409) {
        setConflict(msg);
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-4">
      <Card className="w-full max-w-md shadow-xl border-emerald-100">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
            <Ticket className="w-8 h-8 text-emerald-700" />
          </div>
          <CardTitle className="text-2xl">Seat Booking</CardTitle>
          <p className="text-sm text-slate-500">Anjuman e Saifee · Nagpur</p>
        </CardHeader>
        <CardContent>
          {conflict && (
            <div
              className="mb-3 p-3 rounded-md border border-rose-300 bg-rose-50 text-rose-800 text-sm flex items-start gap-2"
              data-testid="hof-login-conflict"
              role="alert"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>{conflict}</div>
            </div>
          )}
          <form onSubmit={submit} className="space-y-3" data-testid="hof-login-form">
            <div>
              <Label>HOF ITS Number</Label>
              <Input value={its} onChange={(e) => setIts(e.target.value)} placeholder="8-digit ITS" autoFocus required data-testid="hof-its" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="ITS number for first login" required data-testid="hof-pwd" />
              <p className="text-xs text-slate-500 mt-1">First-time users: password is your ITS number.</p>
            </div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={busy} data-testid="hof-login-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LogIn className="w-4 h-4 mr-1" />}
              Sign In
            </Button>
          </form>
          <p className="text-xs text-center text-slate-400 mt-4">
            Single-device login. Log out from your other device before signing in here.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== PASSWORD CHANGE DIALOG ==============
const PasswordChangeDialog = ({ open, token, onClose, mandatory }) => {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (nw.length < 4) { toast.error('New password must be at least 4 characters'); return; }
    if (nw !== confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await hofApi(token).post('/seat/hof/change-password', { current_password: cur, new_password: nw });
      toast.success('Password changed');
      // Update stored info to clear must_change
      try {
        const info = JSON.parse(localStorage.getItem(HOF_INFO_KEY) || '{}');
        info.must_change_password = false;
        localStorage.setItem(HOF_INFO_KEY, JSON.stringify(info));
      } catch {}
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Change failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !mandatory && onClose()}>
      <DialogContent className="max-w-sm" data-testid="hof-pwd-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4" /> {mandatory ? 'Set a new password' : 'Change Password'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {mandatory && <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">For security, please set a new password before booking.</p>}
          <div><Label className="text-xs">Current Password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required /></div>
          <div><Label className="text-xs">New Password (min 4 chars)</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} required /></div>
          <div><Label className="text-xs">Confirm New</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></div>
          <DialogFooter>
            {!mandatory && <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>}
            <Button type="submit" disabled={busy} data-testid="hof-pwd-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== FLOOR MAP (HOF view — no seat numbers) ==============
const HofFloorMap = ({ layout, onPickSeat, pickedSeatNo }) => {
  // Cell size — bigger touch target on small screens
  const [zoom, setZoom] = useState(1);
  const baseCell = 28; // px
  const cellSize = Math.round(baseCell * zoom);
  const seatByCell = {};
  (layout.seats || []).forEach(s => { seatByCell[`${s.row},${s.col}`] = s; });
  const disabledByCell = new Set((layout.disabled_cells || []).map(c => `${c.row},${c.col}`));
  return (
    <div className="space-y-2" data-testid="hof-floor-map-wrap">
      {/* Mobile controls */}
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-slate-500">Pinch / scroll to navigate</span>
        <div className="inline-flex rounded-md border bg-white overflow-hidden" data-testid="hof-zoom-controls">
          <button type="button" onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} className="px-2 py-1 text-sm hover:bg-slate-100" data-testid="hof-zoom-out">−</button>
          <span className="px-2 py-1 text-xs font-mono border-l border-r bg-slate-50">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom(z => Math.min(2.5, +(z + 0.25).toFixed(2)))} className="px-2 py-1 text-sm hover:bg-slate-100" data-testid="hof-zoom-in">+</button>
          <button type="button" onClick={() => setZoom(1)} className="px-2 py-1 text-[11px] hover:bg-slate-100 border-l">Reset</button>
        </div>
      </div>
      <div className="overflow-auto p-2 sm:p-3 bg-slate-50 rounded border touch-pan-x touch-pan-y" data-testid="hof-floor-map" style={{ maxHeight: '70vh' }}>
        <div className="inline-block min-w-full">
          <div className="text-center text-[11px] font-semibold text-slate-500 mb-2 select-none">← QIBLA →</div>
          <div className="grid gap-[3px] select-none mx-auto" style={{ gridTemplateColumns: `repeat(${layout.cols}, ${cellSize}px)`, width: 'fit-content' }}>
            {Array.from({ length: layout.rows }).map((_, r) =>
              Array.from({ length: layout.cols }).map((__, c) => {
                const key = `${r},${c}`;
                const seat = seatByCell[key];
                const isDis = disabledByCell.has(key);
                let bg = 'bg-transparent border-transparent';
                let title = '';
                if (isDis) { bg = 'bg-slate-300 border-slate-400'; title = 'Passage'; }
                else if (seat) {
                  if (seat.is_own) { bg = 'bg-blue-500 hover:bg-blue-600 text-white border-blue-700'; title = `Your booking: ${seat.own_member_name}`; }
                  else if (seat.booked) { bg = 'bg-red-500 border-red-700 text-white'; title = 'Booked'; }
                  else if (pickedSeatNo === seat.seat_no) { bg = 'bg-amber-400 border-amber-700 ring-2 ring-amber-500'; title = 'Selected'; }
                  else if (seat.locked && !seat.locked_by_self) { bg = 'bg-orange-400 border-orange-600 text-white cursor-not-allowed'; title = 'Held by another user'; }
                  else if (seat.locked && seat.locked_by_self) { bg = 'bg-amber-400 border-amber-700 ring-2 ring-amber-500'; title = 'Your hold'; }
                  else { bg = 'bg-emerald-500 hover:bg-emerald-600 cursor-pointer border-emerald-700 text-white'; title = 'Available — tap to book'; }
                }
                const showLabel = false; // Seat numbers hidden from users — admin sees them only.
                return (
                  <button
                    key={key}
                    type="button"
                    className={`border rounded leading-none transition-colors flex items-center justify-center ${bg}`}
                    style={{ width: cellSize, height: cellSize, fontSize: Math.max(8, Math.round(cellSize * 0.32)) }}
                    title={title}
                    onClick={() => seat && !seat.booked && !(seat.locked && !seat.locked_by_self) && onPickSeat(seat)}
                    disabled={!seat || (seat.booked && !seat.is_own) || (seat.locked && !seat.locked_by_self)}
                    data-testid={seat ? `hof-seat-${seat.seat_no}` : undefined}
                  >
                    {seat?.is_own ? '✓' : (seat?.locked && !seat?.locked_by_self ? '·' : '')}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-500" /> Available</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500" /> Booked</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500" /> Your Booking</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-400" /> Selected</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-orange-400" /> On Hold</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-slate-300" /> Passage</span>
      </div>
    </div>
  );
};

// ============== BOOK CONFIRM DIALOG ==============
const ConfirmBookDialog = ({ open, onClose, seat, layout, ladies, onConfirm, busy, preselectedLady }) => {
  const [picked, setPicked] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(seat?.lock_seconds_remaining ?? 180);
  useEffect(() => {
    if (!open) return;
    if (preselectedLady?.id) {
      setPicked(preselectedLady.id);
    } else if (ladies.length === 1) {
      setPicked(ladies[0].id);
    }
  }, [open, ladies, preselectedLady]);

  // Hold-timer countdown — auto-closes the dialog when the lock expires
  useEffect(() => {
    if (!open || !seat?.lock_id) return undefined;
    setSecondsLeft(seat?.lock_seconds_remaining ?? 180);
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          toast.error('Your seat hold expired. Please select again.');
          onClose?.();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [open, seat?.lock_id, seat?.lock_seconds_remaining, onClose]);

  // Always lock when we know which lady the user is booking for.
  const lockedLady = preselectedLady || null;
  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="hof-confirm-book">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span>Confirm Booking</span>
            {seat?.lock_id && (
              <span
                className="inline-flex items-center gap-1 text-xs font-mono text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded"
                data-testid="hof-lock-countdown"
              >
                <Clock className="w-3 h-3" /> hold expires in {mins}:{secs}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 rounded p-2">
            <div className="font-semibold">{layout?.name}</div>
            <div className="text-xs text-slate-500">
              {layout?.is_first_floor_like ? 'First-floor rules apply (age ≥ ' + (layout?.min_age ?? '40') + ', no direct child < ' + (layout?.no_child_under ?? '10') + ')' : 'Standard floor'}
            </div>
          </div>
          {lockedLady ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-sm" data-testid="hof-locked-lady">
              <div className="text-[10px] uppercase font-semibold text-emerald-700 tracking-wider">Booking For</div>
              <div className="font-semibold">{lockedLady.full_name}</div>
              <div className="text-xs text-slate-600 font-mono">ITS {lockedLady.its_id} · age {lockedLady.age ?? '—'}</div>
            </div>
          ) : (
            <div>
              <Label>Select lady to book</Label>
              <select
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                className="w-full border rounded px-2 py-2 text-sm bg-white mt-1"
                data-testid="hof-pick-lady"
              >
                <option value="">— Choose —</option>
                {ladies.map(l => (
                  <option key={l.id} value={l.id}>{l.full_name}{l.age ? ` (age ${l.age})` : ''}</option>
                ))}
              </select>
              {ladies.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">No eligible Misaq Done ladies in your family.</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!picked || busy} onClick={() => onConfirm(picked)} data-testid="hof-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Book this Seat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== HOME / DASHBOARD ==============
const HofHome = ({ token, hof, mustChangePassword, onLogout }) => {
  const [me, setMe] = useState(null);
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [floors, setFloors] = useState([]);
  const [activeFloor, setActiveFloor] = useState(null);
  const [layout, setLayout] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [cancelRequests, setCancelRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [pickedSeat, setPickedSeat] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [submittingCancel, setSubmittingCancel] = useState(false);
  const [preselectedLady, setPreselectedLady] = useState(null);
  const [view, setView] = useState('dashboard');     // 'dashboard' | 'book'
  const [downloadingPassFor, setDownloadingPassFor] = useState(null);
  const bookSectionRef = React.useRef(null);

  const api = hofApi(token);

  // Auto-logout on any 401 from a HOF API call (session was invalidated server-side)
  useEffect(() => {
    if (!token) return undefined;
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        const s = err?.response?.status;
        if (s === 401) {
          toast.error(err?.response?.data?.detail || 'Your session ended. Please sign in again.');
          onLogout();
        } else if (s === 429) {
          // Anti-refresh — do NOT logout; just inform the user
          toast.error(
            err?.response?.data?.detail || 'Too many requests detected. Please wait a few moments.',
            { duration: 4000 },
          );
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // === Single-login enforcement: heartbeat + idle auto-logout + 401 invalidation ===
  // - Sends a heartbeat every 30s so the server keeps the session alive.
  // - Tracks user activity (mouse/keyboard/touch). After 15 min idle → auto-logout.
  // - On any 401 from any HOF API call, logs the user out (their session was invalidated server-side).
  // - On browser close (beforeunload), best-effort calls the logout endpoint.
  const lastActivityRef = React.useRef(Date.now());
  const HEARTBEAT_MS = 30 * 1000;
  const IDLE_LIMIT_MS = 15 * 60 * 1000;

  useEffect(() => {
    const bump = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(ev => window.addEventListener(ev, bump, { passive: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, bump));
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      // Idle auto-logout
      if (Date.now() - lastActivityRef.current > IDLE_LIMIT_MS) {
        stopped = true;
        toast.info('You were logged out due to inactivity.');
        onLogout();
        return;
      }
      try {
        await api.post('/seat/hof/heartbeat');
      } catch (err) {
        if (err?.response?.status === 401) {
          stopped = true;
          toast.error(err?.response?.data?.detail || 'Session ended on another device.');
          onLogout();
          return;
        }
      }
    };
    // Fire one immediately so first-page-load also bumps last_seen, then every 30s
    tick();
    const id = setInterval(tick, HEARTBEAT_MS);
    return () => { stopped = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Best-effort logout on tab close (release the seat for others quickly)
  useEffect(() => {
    if (!token) return undefined;
    const onBeforeUnload = () => {
      try {
        const url = `${API_BASE}/seat/hof/logout`;
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        // sendBeacon doesn't send custom headers, so the server will return 401 — that's fine,
        // because the 2-min heartbeat grace will release the session naturally.
        navigator.sendBeacon?.(url, blob);
      } catch {}
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [token]);

  // Per-lady "Book Now" trigger from the Eligible Members table — switches to BOOK view
  const bookForLady = async (lady) => {
    if (!lady?.eligible) return;
    if (me?.hof_clearance !== 'approved') {
      toast.error('Jamaat Clearance not approved yet');
      return;
    }
    setPreselectedLady(lady);
    setView('book');
    // Auto-pick a single OPEN event if available; otherwise surface the event picker.
    const openEvents = (events || []).filter(e => e.booking_open);
    if (openEvents.length === 1) {
      await pickEvent(openEvents[0]);
    }
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  // Helpers to fetch a pass PDF blob (used by table Print/Download actions)
  const fetchPassBlob = async (booking) => {
    const res = await api.get(`/seat/hof/bookings/${booking.id}/pass`, { responseType: 'blob' });
    return new Blob([res.data], { type: 'application/pdf' });
  };
  const downloadPassForBooking = async (booking) => {
    try {
      setDownloadingPassFor(booking.id);
      const blob = await fetchPassBlob(booking);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pass_${booking.its_id || ''}_seat${booking.seat_no}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download pass');
    } finally { setDownloadingPassFor(null); }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [m, e, b, cr] = await Promise.all([
        api.get('/seat/hof/me'),
        api.get('/seat/hof/events'),
        api.get('/seat/hof/bookings'),
        api.get('/seat/hof/cancellation-requests'),
      ]);
      setMe(m.data);
      setEvents(e.data?.events || []);
      setBookings(b.data?.bookings || []);
      setCancelRequests(cr.data?.requests || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error(err.response.data?.detail || 'Session expired');
        onLogout();
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto re-check session every 30s
  useEffect(() => {
    const t = setInterval(async () => {
      try { await api.get('/seat/hof/me'); }
      catch (err) {
        if (err?.response?.status === 401) {
          toast.error('Session invalidated — signed in on another device');
          onLogout();
        }
      }
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const pickEvent = async (e) => {
    setActiveEvent(e); setActiveFloor(null); setLayout(null); setFloors([]);
    try {
      const res = await api.get(`/seat/hof/events/${e.id}/floors`);
      setFloors(res.data?.floors || []);
    } catch { toast.error('Failed to load floors'); }
  };
  const pickFloor = async (f) => {
    setActiveFloor(f);
    setLayout(null);
    // Release any prior lock when switching floors
    if (pickedSeat?.lock_id) {
      api.delete(`/seat/hof/locks/${pickedSeat.lock_id}`).catch(() => {});
    }
    setPickedSeat(null);
    try {
      const res = await api.get(`/seat/hof/floors/${f.id}/layout`);
      setLayout({
        ...(res.data?.floor || {}),
        __booking_open: res.data?.booking_open !== false,
        __booking_message: res.data?.booking_message || '',
      });
    } catch { toast.error('Failed to load layout'); }
  };

  // === Real-time floor polling (5s) while user is on the book/floor view ===
  useEffect(() => {
    if (!activeFloor?.id || view !== 'book') return undefined;
    let stopped = false;
    const fetchLayout = async () => {
      if (stopped) return;
      try {
        const res = await api.get(`/seat/hof/floors/${activeFloor.id}/layout`);
        if (!stopped) setLayout({
          ...(res.data?.floor || {}),
          __booking_open: res.data?.booking_open !== false,
          __booking_message: res.data?.booking_message || '',
        });
      } catch { /* swallow polling errors */ }
    };
    const id = setInterval(fetchLayout, 5000);
    return () => { stopped = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFloor?.id, view]);

  // Try to acquire an atomic 3-min seat lock when the user clicks a seat.
  // First-click-wins via backend unique index — siblings get 409 → spec popup.
  const onPickSeat = async (seat) => {
    if (!activeFloor?.id || !seat) return;
    // Hard-stop if the booking window is in the future / closed
    if (layout && layout.__booking_open === false) {
      toast.error(layout.__booking_message || 'Booking is not open right now.');
      return;
    }
    // Release any existing lock first (cheap; ignored if none)
    if (pickedSeat?.lock_id && pickedSeat.seat_no !== seat.seat_no) {
      api.delete(`/seat/hof/locks/${pickedSeat.lock_id}`).catch(() => {});
    }
    try {
      const res = await api.post('/seat/hof/locks', {
        floor_id: activeFloor.id,
        seat_no: seat.seat_no,
      });
      const lk = res.data?.lock || {};
      setPickedSeat({
        ...seat,
        lock_id: lk.id,
        lock_seconds_remaining: lk.seconds_remaining ?? 180,
        expires_at: lk.expires_at,
      });
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || 'Could not select this seat — try another.';
      if (status === 409) {
        toast.error(msg, { duration: 4000 });
        // Refresh layout immediately so the seat shows as taken
        api.get(`/seat/hof/floors/${activeFloor.id}/layout`)
          .then(r => setLayout(r.data?.floor))
          .catch(() => {});
      } else {
        toast.error(msg);
      }
    }
  };

  // Release the lock if the user backs out of the picked-seat dialog
  const releasePickedLock = useCallback(() => {
    if (pickedSeat?.lock_id) {
      api.delete(`/seat/hof/locks/${pickedSeat.lock_id}`).catch(() => {});
    }
    setPickedSeat(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSeat?.lock_id]);

  const confirmBook = async (memberId) => {
    if (!pickedSeat) return;
    setConfirming(true);
    try {
      await api.post('/seat/hof/book', {
        floor_id: activeFloor.id,
        seat_no: pickedSeat.seat_no,
        member_id: memberId,
        lock_id: pickedSeat.lock_id,
      });
      toast.success('Seat booked successfully');
      setPickedSeat(null);
      // refresh bookings + me (so the table's "Booked" badge updates)
      const [, r2, meRes] = await Promise.all([
        api.get(`/seat/hof/floors/${activeFloor.id}/layout`),
        api.get('/seat/hof/bookings'),
        api.get('/seat/hof/me'),
      ]);
      setBookings(r2.data?.bookings || []);
      setMe(meRes.data);
      // Auto-return to family dashboard after a successful booking
      setActiveEvent(null);
      setActiveFloor(null);
      setLayout(null);
      setPreselectedLady(null);
      setView('dashboard');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Booking failed');
    } finally { setConfirming(false); }
  };

  const submitCancellationRequest = async () => {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) { toast.error('Please add a reason'); return; }
    setSubmittingCancel(true);
    try {
      await api.post('/seat/hof/cancellation-requests', { booking_id: cancelTarget.id, reason: cancelReason.trim() });
      toast.success('Cancellation request submitted — awaiting admin approval');
      setCancelTarget(null);
      setCancelReason('');
      const r = await api.get('/seat/hof/cancellation-requests');
      setCancelRequests(r.data?.requests || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit');
    } finally { setSubmittingCancel(false); }
  };

  const [passPreview, setPassPreview] = useState(null); // booking object for PDF preview modal
  const printPass = (booking) => setPassPreview(booking);

  // Terms & Conditions acceptance
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const acceptTerms = async () => {
    if (!me?.terms?.id) return;
    setAcceptingTerms(true);
    try {
      await api.post('/seat/hof/accept-terms', { terms_id: me.terms.id });
      toast.success('Terms accepted');
      // Re-fetch me to update terms.accepted flag
      const m = await api.get('/seat/hof/me');
      setMe(m.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept terms');
    } finally { setAcceptingTerms(false); }
  };

  // Format helpers for booking window
  const fmtDateTime = (s) => {
    if (!s) return '—';
    try {
      const d = new Date(s);
      return d.toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return s; }
  };
  // Used by "Book Now" button when booking hasn't started yet
  const handleBookNow = (evt) => {
    if (evt.booking_open) {
      pickEvent(evt);
    } else {
      // Show booking window message in a toast / alert
      toast.message(evt.booking_message || 'Booking is not currently open', {
        description: evt.booking_start ? `Booking will start on ${fmtDateTime(evt.booking_start)}` : undefined,
        duration: 6000,
      });
    }
  };

  const pendingByBooking = React.useMemo(() => {
    const m = {};
    (cancelRequests || []).filter(r => r.status === 'pending').forEach(r => { m[r.booking_id] = r; });
    return m;
  }, [cancelRequests]);

  const logout = async () => {
    try { await api.post('/seat/hof/logout'); } catch {}
    onLogout();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/50 via-white to-amber-50/50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center"><Ticket className="w-5 h-5 text-emerald-700" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate" data-testid="hof-name">{hof.hof_name}</div>
            <div className="text-xs text-slate-500">ITS {hof.hof_its_id} · {hof.sector || '—'}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowPwd(true)} data-testid="hof-change-pwd-btn">
            <Lock className="w-3 h-3 mr-1" /> Password
          </Button>
          <Button variant="outline" size="sm" onClick={logout} data-testid="hof-logout-btn">
            <LogOut className="w-3 h-3 mr-1" /> Logout
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {view === 'book' && (
          <Button variant="outline" size="sm" onClick={() => { setView('dashboard'); setActiveEvent(null); setActiveFloor(null); setLayout(null); setPreselectedLady(null); }} data-testid="back-to-family-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Family Members
          </Button>
        )}

        {view === 'dashboard' && (<>
        {/* HOF Details Card */}
        <Card className="border-emerald-100 shadow-sm">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-md">
              <Home className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-slate-500">Head of Family</div>
              <div className="font-bold text-lg truncate" data-testid="hof-detail-name">{hof.hof_name}</div>
              <div className="text-xs text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                <span><strong>ITS:</strong> <span className="font-mono">{hof.hof_its_id}</span></span>
                <span><strong>Family Members:</strong> {me?.family_member_count ?? '—'}</span>
                {hof.sector && <span><strong>Sector:</strong> {hof.sector}</span>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Eligible Members table */}
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-emerald-600" />
              <h2 className="text-base font-bold text-slate-800">Eligible Members</h2>
              <Badge variant="outline" className="ml-auto text-xs">
                {(me?.ladies_dashboard || []).filter(l => l.eligible).length} eligible / {(me?.ladies_dashboard || []).length} ladies
              </Badge>
            </div>
            {(!me?.ladies_dashboard || me.ladies_dashboard.length === 0) ? (
              <div className="text-center text-sm text-slate-500 py-8">No female members found in your family.</div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Name</th>
                        <th className="px-3 py-2 text-left font-semibold">ITS</th>
                        <th className="px-3 py-2 text-center font-semibold">Age</th>
                        <th className="px-3 py-2 text-center font-semibold">Misaq</th>
                        <th className="px-3 py-2 text-center font-semibold">Jamaat Clearance</th>
                        <th className="px-3 py-2 text-center font-semibold">Booking Eligibility</th>
                        <th className="px-3 py-2 text-center font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {me.ladies_dashboard.map(l => {
                        const myBooking = bookings.find(b => b.its_id === l.its_id);
                        return (
                        <tr key={l.id} className={l.eligible ? 'bg-emerald-50/40' : (l.restriction ? 'bg-rose-50/40' : '')} data-testid={`hof-lady-row-${l.its_id}`}>
                          <td className="px-3 py-2 font-medium">{l.full_name}</td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-600">{l.its_id || '—'}</td>
                          <td className="px-3 py-2 text-center">{l.age ?? '—'}</td>
                          <td className="px-3 py-2 text-center">
                            {l.misaq_status === 'Done' ? (
                              <Badge className="bg-emerald-600 text-[10px]">Done</Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">{l.misaq_status}</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {l.hof_clearance_status === 'approved' ? (
                              <Badge className="bg-emerald-600 text-[10px]"><ShieldCheck className="w-3 h-3 mr-0.5" /> Approved</Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]"><ShieldAlert className="w-3 h-3 mr-0.5" /> Pending</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {l.eligible ? (
                              <Badge className="bg-emerald-600 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-0.5" /> Eligible</Badge>
                            ) : l.restriction ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <Badge variant="outline" className="text-rose-700 border-rose-400 bg-rose-50 text-[10px]"><XCircle className="w-3 h-3 mr-0.5" /> Not Eligible</Badge>
                                <span className="text-[10px] text-rose-700 font-semibold">{l.restriction.reason_label}</span>
                              </div>
                            ) : (
                              <span title={l.ineligible_reasons?.join(' · ')}>
                                <Badge variant="outline" className="text-red-700 border-red-300 text-[10px]"><XCircle className="w-3 h-3 mr-0.5" /> Not Eligible</Badge>
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {myBooking ? (
                              <div className="inline-flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => printPass(myBooking)} className="h-7 text-xs" data-testid={`hof-print-${l.its_id}`}>
                                  <Printer className="w-3 h-3 mr-1" /> Print
                                </Button>
                                <Button size="sm" variant="outline" disabled={downloadingPassFor === myBooking.id} onClick={() => downloadPassForBooking(myBooking)} className="h-7 text-xs" data-testid={`hof-download-${l.its_id}`}>
                                  {downloadingPassFor === myBooking.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
                                  Download
                                </Button>
                              </div>
                            ) : l.eligible ? (
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                onClick={() => bookForLady(l)}
                                data-testid={`hof-book-for-${l.its_id}`}
                              >
                                <Ticket className="w-3 h-3 mr-1" /> Book Now
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" disabled className="h-7 text-xs opacity-50">
                                Book Now
                              </Button>
                            )}
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="sm:hidden space-y-2">
                  {me.ladies_dashboard.map(l => {
                    const myBooking = bookings.find(b => b.its_id === l.its_id);
                    return (
                    <div key={l.id} className={`border rounded p-3 ${l.eligible ? 'bg-emerald-50/40 border-emerald-200' : l.restriction ? 'bg-rose-50/40 border-rose-200' : 'bg-white'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{l.full_name}</div>
                          <div className="text-xs text-slate-500 font-mono">{l.its_id || '—'} · age {l.age ?? '—'}</div>
                        </div>
                        {l.eligible ? (
                          <Badge className="bg-emerald-600 text-[10px]">Eligible</Badge>
                        ) : (
                          <Badge variant="outline" className={`text-[10px] ${l.restriction ? 'text-rose-700 border-rose-400' : 'text-red-700 border-red-300'}`}>Not Eligible</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                        <Badge variant={l.misaq_status === 'Done' ? 'default' : 'outline'}
                          className={l.misaq_status === 'Done' ? 'bg-emerald-600' : 'text-amber-700 border-amber-300'}>
                          Misaq: {l.misaq_status}
                        </Badge>
                        <Badge variant="outline" className={l.hof_clearance_status === 'approved' ? 'text-emerald-700 border-emerald-300' : 'text-amber-700 border-amber-300'}>
                          {l.hof_clearance_status === 'approved' ? 'Jamaat Approved' : 'Jamaat Pending'}
                        </Badge>
                        {l.restriction && (
                          <Badge variant="outline" className="text-rose-700 border-rose-400 bg-rose-50">
                            {l.restriction.reason_label}
                          </Badge>
                        )}
                      </div>
                      {!l.eligible && l.ineligible_reasons?.length > 0 && (
                        <div className={`text-[11px] mt-1.5 ${l.restriction ? 'text-rose-700 font-medium' : 'text-red-700'}`}>{l.ineligible_reasons.join(' · ')}</div>
                      )}
                      {myBooking ? (
                        <div className="mt-2 grid grid-cols-2 gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => printPass(myBooking)} data-testid={`hof-print-mobile-${l.its_id}`}>
                            <Printer className="w-3 h-3 mr-1" /> Print Pass
                          </Button>
                          <Button size="sm" variant="outline" disabled={downloadingPassFor === myBooking.id} onClick={() => downloadPassForBooking(myBooking)} data-testid={`hof-download-mobile-${l.its_id}`}>
                            {downloadingPassFor === myBooking.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
                            Download
                          </Button>
                        </div>
                      ) : l.eligible && (
                        <Button
                          size="sm"
                          className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => bookForLady(l)}
                          data-testid={`hof-book-for-mobile-${l.its_id}`}
                        >
                          <Ticket className="w-3 h-3 mr-1" /> Book Now
                        </Button>
                      )}
                    </div>
                  );})}
                </div>
              </>
            )}
            {/* Notice if any lady is restricted */}
            {(me?.ladies_dashboard || []).some(l => l.restriction) && (
              <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded text-xs text-rose-900" data-testid="hof-restricted-notice">
                <strong>Note:</strong> One or more members in your family are not eligible for seat booking due to active restrictions.
                Please contact your Jamaat Office for further assistance.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Jamaat Clearance Gate */}
        {me?.hof_clearance !== 'approved' ? (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="p-4 flex items-start gap-3" data-testid="hof-clearance-gate-not-approved">
              <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-red-900">Jamaat Clearance Not Done</h3>
                <p className="text-sm text-red-800 mt-1">
                  Please contact your Jamaat Office for clearance. Seat booking will be available once your HOF clearance is approved.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-3 flex items-center gap-2" data-testid="hof-clearance-gate-approved">
              <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-emerald-900">Jamaat Clearance Approved</div>
                <div className="text-xs text-emerald-800">You may proceed to book seats below.</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Terms & Conditions removed per request — booking flows directly after Jamaat Clearance */}
        </>)}

        {/* Book Your Seat — shown only in 'book' view; entered via per-lady Book Now */}
        {view === 'book' && me?.hof_clearance === 'approved' && (
          <Card ref={bookSectionRef}>
            <CardContent className="p-3 sm:p-4 space-y-3" data-testid="hof-book-section">
              <div className="flex items-center gap-2 flex-wrap">
                <Ticket className="w-5 h-5 text-emerald-600" />
                <h2 className="text-base font-bold text-slate-800">Book Your Seat</h2>
                {preselectedLady && (
                  <div className="ml-auto flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-xs" data-testid="preselected-lady-banner">
                    <span className="text-emerald-700">Booking for:</span>
                    <strong className="text-emerald-900">{preselectedLady.full_name}</strong>
                    <button onClick={() => setPreselectedLady(null)} className="text-emerald-700 hover:text-emerald-900">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              {events.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No events open for booking yet.</div>
              ) : !activeEvent ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {events.map(e => (
                    <Card key={e.id} className={`overflow-hidden transition-all ${e.booking_open ? 'border-emerald-300 hover:shadow-md' : 'border-slate-200'}`} data-testid={`hof-event-${e.id}`}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-bold truncate">{e.name}</div>
                            <div className="text-[11px] text-slate-500 line-clamp-2">{e.description || '—'}</div>
                          </div>
                          {e.booking_open ? (
                            <Badge className="bg-emerald-600 text-[10px]">OPEN</Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">CLOSED</Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-600 space-y-0.5 bg-slate-50 p-2 rounded border">
                          <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> <strong>Start:</strong> <span>{fmtDateTime(e.booking_start)}</span></div>
                          <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> <strong>End:</strong> <span>{fmtDateTime(e.booking_end)}</span></div>
                        </div>
                        <Button
                          className={`w-full ${e.booking_open ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 hover:bg-slate-500'}`}
                          onClick={() => handleBookNow(e)}
                          data-testid={`hof-book-now-${e.id}`}
                        >
                          <Ticket className="w-4 h-4 mr-1" /> Book Now
                        </Button>
                        {!e.booking_open && e.booking_start && (
                          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5 text-center">
                            Seat Booking will start on <strong>{fmtDateTime(e.booking_start)}</strong>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : !activeFloor ? (
                <Card>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setActiveEvent(null)}><ArrowLeft className="w-4 h-4" /></Button>
                      <div className="flex-1"><div className="font-bold">{activeEvent.name}</div><div className="text-xs text-slate-500">Choose a floor</div></div>
                    </div>
                    {floors.length === 0 ? (
                      <div className="text-sm text-slate-500 py-6 text-center">No floors configured yet.</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {floors.map(f => (
                          <button
                            key={f.id}
                            onClick={() => pickFloor(f)}
                            className="text-left p-3 border-2 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 rounded-lg"
                            data-testid={`hof-floor-${f.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-blue-600" />
                              <div className="font-semibold">{f.name}</div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {f.available_count} available / {f.seat_count} seats
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {f.is_first_floor_like && <Badge variant="outline" className="text-[9px]">1st Floor Rules</Badge>}
                              {f.hof_seat_limit > 0 && <Badge variant="outline" className="text-[9px]">{f.hof_seat_limit} per HOF</Badge>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setActiveFloor(null); setLayout(null); }}><ArrowLeft className="w-4 h-4" /></Button>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{activeFloor.name}</div>
                        <div className="text-xs text-slate-500">{activeEvent.name}</div>
                      </div>
                    </div>
                    {!layout ? (
                      <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
                    ) : layout.__booking_open === false ? (
                      <div className="p-6 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="booking-closed-banner">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-700" />
                          <div>
                            <div className="font-semibold text-base">Booking is not open right now</div>
                            <div className="mt-1">{layout.__booking_message || 'Please come back during the booking window.'}</div>
                            <div className="mt-3 text-xs text-amber-700">
                              You can browse the floor but cannot pick seats until the booking window starts.
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {layout.is_first_floor_like && (
                          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>
                              First-floor rules: lady age ≥ {layout.min_age ?? 40}, no direct child &lt; {layout.no_child_under ?? 10}, max {layout.hof_seat_limit || '—'} per HOF.
                              Your current bookings here: {layout.own_count}.
                            </div>
                          </div>
                        )}
                        <HofFloorMap layout={layout} onPickSeat={onPickSeat} pickedSeatNo={pickedSeat?.seat_no} />
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}

        {view === 'dashboard' && (<>
        {/* Your bookings */}
        {bookings.length > 0 && (
          <Card>
            <CardContent className="p-3">
              <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600" /> Your Bookings
              </div>
              <div className="space-y-2">
                {bookings.map(b => {
                  const pending = pendingByBooking[b.id];
                  return (
                    <div key={b.id} className="p-2 bg-blue-50/50 border border-blue-100 rounded space-y-2" data-testid={`hof-mybooking-${b.id}`}>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-600">Booked</Badge>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{b.member_name}</div>
                          <div className="text-xs text-slate-500">{b.event_name} · {b.floor_name}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => printPass(b)} data-testid={`hof-print-${b.id}`}>
                          <Printer className="w-3 h-3 mr-1" /> Print / Download Pass
                        </Button>
                        {pending ? (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 px-2 py-1">
                            <Clock className="w-3 h-3 mr-1" /> Cancellation Pending Approval
                          </Badge>
                        ) : (
                          <Button size="sm" variant="outline" className="text-red-600"
                            onClick={() => { setCancelTarget(b); setCancelReason(''); }}
                            data-testid={`hof-request-cancel-${b.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Request Cancellation
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cancellation request history */}
        {cancelRequests.length > 0 && (
          <Card>
            <CardContent className="p-3">
              <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" /> Cancellation Requests
              </div>
              <div className="space-y-1.5">
                {cancelRequests.map(r => (
                  <div key={r.id} className="flex items-center gap-2 text-sm p-2 border rounded bg-white" data-testid={`hof-cancel-req-${r.id}`}>
                    <Badge className={
                      r.status === 'approved' ? 'bg-emerald-600' :
                      r.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'
                    }>{r.status.toUpperCase()}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.member_name}</div>
                      <div className="text-xs text-slate-500 truncate">"{r.reason || '—'}"  ·  {(r.created_at || '').replace('T', ' ').split('.')[0]}</div>
                      {r.admin_note && <div className="text-xs text-slate-600 italic">Admin: {r.admin_note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        </>)}
      </div>

      <ConfirmBookDialog
        open={!!pickedSeat}
        onClose={releasePickedLock}
        seat={pickedSeat}
        layout={layout}
        ladies={me?.eligible_ladies || []}
        onConfirm={(memberId) => { confirmBook(memberId).then(() => setPreselectedLady(null)); }}
        busy={confirming}
        preselectedLady={preselectedLady}
      />

      {/* Cancellation request dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="hof-cancel-dialog">
          <DialogHeader><DialogTitle>Request Cancellation</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 rounded p-2">
              <div className="font-semibold">{cancelTarget?.member_name}</div>
              <div className="text-xs text-slate-500">{cancelTarget?.floor_name} · {cancelTarget?.event_name}</div>
            </div>
            <div>
              <Label className="text-xs">Reason for cancellation</Label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                placeholder="e.g. travelling out of station, health reasons…"
                data-testid="hof-cancel-reason"
              />
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
              Your request will be reviewed by the admin. The seat remains booked until approval.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Close</Button>
            <Button disabled={submittingCancel || !cancelReason.trim()} onClick={submitCancellationRequest} data-testid="hof-cancel-submit">
              {submittingCancel ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PasswordChangeDialog
        open={showPwd}
        token={token}
        onClose={() => setShowPwd(false)}
        mandatory={false}
      />

      <PdfPreviewModal
        open={!!passPreview}
        onClose={() => setPassPreview(null)}
        title={passPreview ? `Seat Pass — ${passPreview.event_name || ''} · ${passPreview.floor_name || ''}` : 'Seat Pass'}
        fileName={passPreview ? `seat_pass_${passPreview.event_name || ''}_${passPreview.seat_no}.pdf` : 'seat_pass.pdf'}
        fetcher={() => api.get(`/seat/hof/bookings/${passPreview?.id}/pass`, { responseType: 'blob' })}
      />
    </div>
  );
};

// ============== PAGE ROOT ==============
export default function HofSeatPortal() {
  const [token, setToken] = useState(localStorage.getItem(HOF_TOKEN_KEY) || '');
  const [hof, setHof] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HOF_INFO_KEY) || 'null')?.hof || null; }
    catch { return null; }
  });
  const [mustChange, setMustChange] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HOF_INFO_KEY) || 'null')?.must_change_password || false; }
    catch { return false; }
  });

  const onLoggedIn = (t, h, mc) => { setToken(t); setHof(h); setMustChange(mc); };
  const onLogout = () => {
    localStorage.removeItem(HOF_TOKEN_KEY);
    localStorage.removeItem(HOF_INFO_KEY);
    setToken(''); setHof(null); setMustChange(false);
  };

  if (!token || !hof) return <LoginView onLoggedIn={onLoggedIn} />;
  return <HofHome token={token} hof={hof} mustChangePassword={mustChange} onLogout={onLogout} />;
}
